import Link from 'next/link';
import React from 'react';

import handleGA4Event, { eventTracker } from '@/lib/service-workers';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Card, { CardAction, CardContent } from '@/components/@base/card';
import Container from '@/components/@base/container';
import { Heading, Text } from '@/components/@base/typography';
import { HEADING_TYPE } from '@/components/@base/typography/heading/type';
import { TEXT_TYPE } from '@/components/@base/typography/text/type';
import XImage from '@/components/@base/x-image';
import Separator from '@/components/app/archive/components/separator';
import { MARKETS } from '@/components/app/product/components/@shared/type';
import { isBundle } from '@/components/app/product/helpers';
import { useGetProfile } from '@/components/layout/services/hooks';
import Actions from '@/components/shared/components/cards/@shared/actions';
import { SequentialIcons } from '@/components/shared/components/icon';
import IconTextCombo from '@/components/shared/components/icon-text-combo';
import PriceView from '@/components/shared/components/price-view';
import { toFixedWithoutZeros } from '@/components/shared/helpers';

import imagesList from '@/constant/images-list';
import localization from '@/constant/localization';
import ProductModel from '@/models/product.model';
import { DATA_CY_PRODUCT_LIST_CARD } from '@/constant/cypress';

export default function RowProductCard({
  title,
  cover,
  shortDescription,
  discount,
  store,
  icon,
  medals,
  slug,
  price,
  id,
  lastPrice,
  rate,
  saleAmount,
  sellable,
  saleEndedAt,
  categories,
  market,
  isOnSale,
  hasAddonFreeProduct,
  ...props //TODO: remove this props if it's unnecessary
}: ProductModel) {
  const finalDiscount = isOnSale ? discount : 0;

  const productRate = rate ?? { rateAverage: 0, rateCount: 0 };
  const { profile } = useGetProfile();
  const clickEventHandler = () => {
    eventTracker(profile?.mobile, 'product_click', new Date().toISOString(), {
      url: window.location?.href,
      category: categories?.map((item) => ({
        $oid: item?.id,
      })),
      vendor: store?.id,
      price: price,
    });
    handleGA4Event('select_item', [
      {
        item_id: id,
        item_name: title,
        price: price,
        index: 0,
        quantity: 1,
        affiliation: store?.slug,
        discount: discount,
      },
    ]);
  };
  const merchantEventHandler = () => {
    eventTracker(profile?.mobile, 'merchant_click', new Date().toISOString(), {
      vendor_name: store?.name,
      vendor_id: store?.id,
    });
  };
  return (
    <Card data-cy={DATA_CY_PRODUCT_LIST_CARD} noBorder onClick={clickEventHandler}>
      <CardContent className='flex gap-4 p-4'>
        <Link href={`/web/${slug}`} prefetch={false}>
          <XImage
            src={cover?.publicLink || imagesList.images.productDefaultCover}
            className='rounded-lg shadow-[0px_5px_10px_0px_#0000000D]'
            alt={cover?.title}
            width={250}
            height={250}
          />
        </Link>

        <Container className='flex flex-1 flex-col justify-around'>
          <Container center className='gap-8'>
            <Container>
              <Link
                href={`/web/${slug}`}
                className='transition-all duration-300 hover:text-[#ff9606]'
                prefetch={false}
              >
                <Heading type={HEADING_TYPE.H3} className='line-clamp-1'>
                  {title}
                </Heading>
              </Link>

              {store && (
                <IconTextCombo
                  textClassName='text-[#878F9B] truncate'
                  Icon={
                    <XImage
                      className='rounded-full border-2 border-[#FFFFFF]'
                      width={30}
                      height={30}
                      alt={store?.logo?.title}
                      src={
                        store?.logo?.publicLink
                          ? store?.logo?.publicLink
                          : imagesList.images.productDefaultCover
                      }
                    />
                  }
                  title={`${localization.vendor} : ${store?.name}`}
                  onClick={merchantEventHandler}
                />
              )}
              <Text className='my-2 line-clamp-2 text-[#878F9B]'>{shortDescription}</Text>
            </Container>
            <Container center className='mr-auto flex-col items-start gap-3.5'>
              <PriceView
                sellable={sellable}
                price={price}
                lastPrice={lastPrice}
                discount={finalDiscount}
                saleEndedAt={saleEndedAt}
                showDiscountLabel={true}
                className='-ml-4 flex items-center gap-3 rounded-s-md bg-[linear-gradient(111.32deg,_#FFDAA266_-1.54%,_#FFF3E066_21.68%)] px-5 py-4'
                priceWrapperClassName='flex flex-col items-end'
              />
              <Container center className='gap-1'>
                <XImage
                  src={imagesList.icons.gradientCartIcon}
                  alt={localization.cart}
                  width={19}
                  height={19}
                />
                <Text
                  type={TEXT_TYPE.SPAN}
                  size={SIZE_ENUM.LG}
                  bold
                  className='text-[#424244]'
                >
                  {saleAmount || 0}
                </Text>
                <Text size={SIZE_ENUM.XS} className='text-[#76767C]'>
                  {localization.sale}
                </Text>
              </Container>
              {productRate?.rateAverage > 0 && (
                <Container center className='gap-1'>
                  <XImage
                    src={imagesList.icons.gradientStarIcon}
                    alt={localization.rate}
                    width={17}
                    height={16}
                  />
                  <Text
                    type={TEXT_TYPE.SPAN}
                    size={SIZE_ENUM.LG}
                    bold
                    className='text-[#424244]'
                  >
                    {toFixedWithoutZeros(productRate?.rateAverage, 1)}
                  </Text>
                  <Text size={SIZE_ENUM.XS} className='text-[#76767C]'>
                    {localization.outOf} {productRate?.rateCount} {localization.vote}
                  </Text>
                </Container>
              )}
            </Container>
          </Container>
          <Separator className='mt-8 mb-4 bg-[#F6F7F8]' />
          <Container center className='justify-between'>
            {medals && (
              <Container className='w-full max-w-[135px]'>
                <SequentialIcons
                  icons={medals}
                  size={36}
                  iconClassName='absolute h-10 w-10 rounded-full border-none'
                  wrapperClassName='relative h-10'
                  shiftAmount={25}
                />
              </Container>
            )}
            <CardAction center className='mr-auto gap-3 p-0'>
              <Actions
                isFree={price === 0 && !hasAddonFreeProduct}
                disabled={!sellable}
                productId={id}
                hasIcon
                slug={slug}
                hasPreviewButton={market !== MARKETS.BUNDLE}
                className='h-[51px] w-[168px]'
              />
            </CardAction>
          </Container>
        </Container>
      </CardContent>
    </Card>
  );
}
